import React, { useState, useEffect } from 'react';

import { Space, Typography, Button, Alert, Tag } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';

import { STATUS_TAG_CONFIG } from '../type';
import KeyIcon from '@/components/icons/KeyIcon';

const { Text } = Typography;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

interface SecurityCodeSectionProps {
  lastInspectionStatus?: {
    status: string;
    date: string;
    time: string;
  };
  securityCode?: string;
}

//manage tag whith status
const getStatusTag = (status: string) => {
  const config =
    STATUS_TAG_CONFIG[status as keyof typeof STATUS_TAG_CONFIG] ||
    STATUS_TAG_CONFIG.NOT_REQUESTED;

  return (
    <Tag color={config.color}>
      <Text
        style={{ color: config.textColor, fontSize: '.75rem', fontWeight: 400 }}
      >
        {config.label}
      </Text>
    </Tag>
  );
};

export const SecurityCodeSection: React.FC<SecurityCodeSectionProps> = ({
  lastInspectionStatus,
  securityCode,
}) => {
  const [showSecurityCode, setShowSecurityCode] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const isMobile = useIsMobile();

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Text
        style={{
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '125%',
        }}
      >
        Security code
      </Text>

      {/* Last know inspection status */}
      {lastInspectionStatus && (
        <Space direction="vertical" size={2}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '125%',
            }}
          >
            Last know inspection status
          </Text>
          <Text
            style={{
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '135%',
              color: '#595959',
            }}
          >
            {getStatusTag(lastInspectionStatus.status)}
            {lastInspectionStatus.status !== 'NOT_REQUESTED' && (
              <>
                on {lastInspectionStatus.date} at {lastInspectionStatus.time}
              </>
            )}
          </Text>
        </Space>
      )}

      {/* Show Carrier's security code button or message if no code */}
      {securityCode ? (
        <Button
          onClick={() => {
            if (!showSecurityCode) {
              setHasClicked(true);
              setShowSecurityCode(true);
            }
          }}
          disabled={showSecurityCode}
          style={{
            width: '100%',
            height: '4.0625rem',
            padding: 0,
            overflow: 'hidden',
            position: 'relative',
            background: '#D9D9D9',
            border: '1px solid #D9D9D9',
            borderRadius: '4px',
            boxShadow: '0px 0px 2px 2px #0000000D',
            cursor: showSecurityCode ? 'not-allowed' : 'pointer',
          }}
        >
          <motion.div
            initial={false}
            animate={{
              width: showSecurityCode ? (isMobile ? '76%' : '83%') : '100%',
              x: showSecurityCode ? '100%' : '0%',
            }}
            transition={
              hasClicked
                ? {
                    type: 'spring',
                    stiffness: 200,
                    damping: 30,
                    mass: 0.8,
                  }
                : { duration: 0 }
            }
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              background: '#FFFFFF',
              borderLeft: '1px solid #D9D9D9',
              borderRadius: '4px',
              zIndex: 0,
              transformOrigin: 'left',
              willChange: 'transform, width',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: showSecurityCode ? 'flex-end' : 'flex-start',
              width: '100%',
              height: '100%',
              position: 'relative',
              zIndex: 1,
              padding: '0 1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.5rem',
                position: 'relative',
              }}
            >
              <AnimatePresence mode="wait">
                {!showSecurityCode && (
                  <motion.div
                    key="icon-left"
                    initial={false}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={
                      hasClicked
                        ? { opacity: 0, scale: 0.8 }
                        : { opacity: 1, scale: 1 }
                    }
                    transition={
                      hasClicked
                        ? {
                            type: 'spring',
                            stiffness: 300,
                            damping: 25,
                          }
                        : { duration: 0 }
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      willChange: 'transform, opacity',
                    }}
                  >
                    <KeyIcon color="#000" />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.div
                  key={showSecurityCode ? 'hide-text' : 'show-text'}
                  initial={false}
                  animate={{ opacity: 1 }}
                  exit={hasClicked ? { opacity: 0 } : { opacity: 1 }}
                  transition={hasClicked ? { duration: 0.2 } : { duration: 0 }}
                  style={{ willChange: 'opacity' }}
                >
                  {!showSecurityCode ? (
                    <Text
                      style={{
                        fontSize: '.875rem',
                        fontWeight: 500,
                        lineHeight: '125%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Show Carrier&apos;s security
                    </Text>
                  ) : (
                    <Space
                      direction="vertical"
                      size={0}
                      align="start"
                      style={{
                        position: 'absolute',
                        right: isMobile ? '70px' : '110px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '.875rem',
                          fontWeight: 500,
                          lineHeight: '125%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Carrier&apos;s security code
                      </Text>
                      <Text
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 500,
                          lineHeight: '130%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {securityCode}
                      </Text>
                    </Space>
                  )}
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {showSecurityCode && (
                  <motion.div
                    key="icon-right"
                    initial={false}
                    animate={
                      hasClicked
                        ? { opacity: 1, scale: 1 }
                        : { opacity: 1, scale: 1 }
                    }
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={
                      hasClicked
                        ? {
                            type: 'spring',
                            stiffness: 300,
                            damping: 25,
                          }
                        : { duration: 0 }
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      willChange: 'transform, opacity',
                    }}
                  >
                    <KeyIcon color="#000" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Button>
      ) : (
        <Text
          style={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '135%',
            color: '#595959',
            fontStyle: 'italic',
          }}
        >
          No inspection code available for this shipment
        </Text>
      )}

      {/* Display security code if toggled */}

      {/* Warning message */}
      <Alert
        type="warning"
        message="Statuses may take some time to update and are not in real-time."
        showIcon
      />
    </Space>
  );
};
